import { Layout } from '../components/Layout';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Users, MapPin, Package, BookOpen, ShieldCheck, Trash2, Pencil, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function AdminSetup({ user, onLogout }) {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [allowlist, setAllowlist] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showLocationForm, setShowLocationForm] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingAllow, setEditingAllow] = useState(false);
  const [allowForm, setAllowForm] = useState({ email: '', name: '', role: 'outlet_staff', outlet_id: '' });
  const [clearConfirm, setClearConfirm] = useState('');
  const [clearing, setClearing] = useState(false);
  const [invConfirm, setInvConfirm] = useState('');
  const [clearingInv, setClearingInv] = useState(false);

  const clearPosSales = async () => {
    if (clearConfirm !== 'CLEAR') return;
    setClearing(true);
    try {
      const res = await axios.post(`${API}/admin/clear-pos-sales`, { confirm: clearConfirm }, getAuthHeader());
      toast.success(`Cleared ${res.data.pos_sales_deleted} POS sale(s)`);
      setClearConfirm('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear data');
    } finally {
      setClearing(false);
    }
  };

  const clearInventoryTestData = async () => {
    if (invConfirm !== 'CLEAR') return;
    setClearingInv(true);
    try {
      const res = await axios.post(`${API}/admin/clear-inventory-test-data`, { confirm: invConfirm }, getAuthHeader());
      toast.success(`Removed ${res.data.test_products_deleted} test product(s), ${res.data.test_menu_items_deleted} test menu item(s) & ${res.data.inventory_txns_deleted} stock record(s)`);
      setInvConfirm('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear data');
    } finally {
      setClearingInv(false);
    }
  };

  const [userForm, setUserForm] = useState({ phone: '', name: '', role: 'outlet_staff', outlet_id: '' });
  const [locationForm, setLocationForm] = useState({ location_id: '', name: '', type: 'outlet' });
  const [productForm, setProductForm] = useState({
    product_id: '',
    name: '',
    category: 'raw_material',
    unit: 'kg',
    sponge_size: '',
    sponge_base: 'chocolate'
  });

  useEffect(() => {
    fetchUsers();
    fetchLocations();
    fetchProducts();
    fetchAllowlist();
  }, []);

  const fetchAllowlist = async () => {
    try {
      const res = await axios.get(`${API}/auth/allowlist`, getAuthHeader());
      setAllowlist(res.data);
    } catch (error) {
      console.error('Failed to fetch allowlist:', error);
    }
  };

  const handleAddAllow = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/allowlist`, allowForm, getAuthHeader());
      toast.success(editingAllow ? 'Access updated' : `Access granted to ${allowForm.email}`);
      setAllowForm({ email: '', name: '', role: 'outlet_staff', outlet_id: '' });
      setEditingAllow(false);
      fetchAllowlist();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add');
    }
  };

  const startEditAllow = (a) => {
    setAllowForm({ email: a.email, name: a.name || '', role: a.role, outlet_id: a.outlet_id || '' });
    setEditingAllow(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteAllow = async (email) => {
    if (!window.confirm(`Revoke Google access for ${email}?`)) return;
    try {
      await axios.delete(`${API}/auth/allowlist/${encodeURIComponent(email)}`, getAuthHeader());
      toast.success('Access revoked');
      fetchAllowlist();
    } catch (error) {
      toast.error('Failed to revoke');
    }
  };

  const locName = (id) => locations.find((l) => l.location_id === id)?.name || '—';

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, getAuthHeader());
      setUsers(res.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`, getAuthHeader());
      setLocations(res.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`, getAuthHeader());
      setProducts(res.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.put(`${API}/users/${encodeURIComponent(editingUser)}`, { name: userForm.name, role: userForm.role, outlet_id: userForm.outlet_id || null }, getAuthHeader());
        toast.success('User updated');
      } else {
        await axios.post(`${API}/users`, userForm, getAuthHeader());
        toast.success('User created successfully');
      }
      setShowUserForm(false);
      setEditingUser(null);
      setUserForm({ phone: '', name: '', role: 'outlet_staff', outlet_id: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const startEditUser = (u) => {
    setUserForm({ phone: u.phone || '', name: u.name || '', role: u.role, outlet_id: u.outlet_id || '' });
    setEditingUser(u.phone || u.email || u.user_id);
    setShowUserForm(true);
  };

  const handleDeleteUser = async (u) => {
    if (!window.confirm(`Delete user ${u.name}?`)) return;
    try {
      await axios.delete(`${API}/users/${encodeURIComponent(u.phone || u.email || u.user_id)}`, getAuthHeader());
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleCreateLocation = async (e) => {
    e.preventDefault();
    try {
      if (editingLocation) {
        await axios.put(`${API}/locations/${encodeURIComponent(editingLocation)}`, { ...locationForm, is_active: true }, getAuthHeader());
        toast.success('Location updated');
      } else {
        await axios.post(`${API}/locations`, { ...locationForm, is_active: true }, getAuthHeader());
        toast.success('Location created successfully');
      }
      setShowLocationForm(false);
      setEditingLocation(null);
      setLocationForm({ location_id: '', name: '', type: 'outlet' });
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    }
  };

  const startEditLocation = (loc) => {
    setLocationForm({ location_id: loc.location_id, name: loc.name, type: loc.type });
    setEditingLocation(loc.location_id);
    setShowLocationForm(true);
  };

  const handleDeleteLocation = async (loc) => {
    if (!window.confirm(`Delete location ${loc.name}?`)) return;
    try {
      await axios.delete(`${API}/locations/${encodeURIComponent(loc.location_id)}`, getAuthHeader());
      toast.success('Location deleted');
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${encodeURIComponent(editingProduct)}`, productForm, getAuthHeader());
        toast.success('Product updated');
      } else {
        await axios.post(`${API}/products`, productForm, getAuthHeader());
        toast.success('Product created successfully');
      }
      setShowProductForm(false);
      setEditingProduct(null);
      setProductForm({
        product_id: '',
        name: '',
        category: 'raw_material',
        unit: 'kg',
        sponge_size: '',
        sponge_base: 'chocolate'
      });
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const startEditProduct = (prod) => {
    setProductForm({
      product_id: prod.product_id,
      name: prod.name,
      category: prod.category,
      unit: prod.unit,
      sponge_size: prod.sponge_size || '',
      sponge_base: prod.sponge_base || 'chocolate',
    });
    setEditingProduct(prod.product_id);
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (prod) => {
    if (!window.confirm(`Delete product ${prod.name}?`)) return;
    try {
      await axios.delete(`${API}/products/${encodeURIComponent(prod.product_id)}`, getAuthHeader());
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold text-stone-900">Admin Setup</h1>
          <p className="text-stone-600 mt-1">Manage users, locations, products, and recipes</p>
        </div>

        <Tabs defaultValue="users" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>
            <TabsTrigger value="locations" data-testid="locations-tab">Locations</TabsTrigger>
            <TabsTrigger value="products" data-testid="products-tab">Products</TabsTrigger>
            <TabsTrigger value="access" data-testid="access-tab">Google Access</TabsTrigger>
            <TabsTrigger value="danger" data-testid="danger-tab">Danger Zone</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <div className="bg-white border border-stone-200 rounded-sm shadow-sm">
              <div className="p-6 border-b border-stone-200 flex justify-between items-center">
                <h2 className="font-heading text-2xl font-semibold text-stone-800">Users</h2>
                <Button onClick={() => setShowUserForm(!showUserForm)} data-testid="add-user-button">
                  <Plus size={20} className="mr-2" />
                  Add User
                </Button>
              </div>

              {showUserForm && (
                <div className="p-6 border-b border-stone-200 bg-stone-50">
                  <form onSubmit={handleCreateUser} className="grid grid-cols-2 gap-4">
                    <input
                      type="tel"
                      placeholder="Phone"
                      data-testid="user-phone-input"
                      value={userForm.phone}
                      onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })}
                      className="h-10 px-3 border border-stone-200 rounded-sm disabled:bg-stone-100 disabled:text-stone-400"
                      required={!editingUser}
                      disabled={!!editingUser}
                    />
                    <input
                      type="text"
                      placeholder="Name"
                      data-testid="user-name-input"
                      value={userForm.name}
                      onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                      className="h-10 px-3 border border-stone-200 rounded-sm"
                      required
                    />
                    <Select value={userForm.role} onValueChange={(value) => setUserForm({ ...userForm, role: value })}>
                      <SelectTrigger data-testid="user-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="manager">Manager</SelectItem>
                        <SelectItem value="central_kitchen">Central Kitchen</SelectItem>
                        <SelectItem value="outlet_staff">Outlet Staff</SelectItem>
                        <SelectItem value="finance">Finance</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={userForm.outlet_id || undefined} onValueChange={(value) => setUserForm({ ...userForm, outlet_id: value })}>
                      <SelectTrigger data-testid="user-location-select">
                        <SelectValue placeholder="Location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.filter((l) => ['outlet', 'central_kitchen'].includes(l.type)).map((l) => (
                          <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Button type="submit" data-testid="save-user-button">{editingUser ? 'Update' : 'Save'}</Button>
                      <Button type="button" variant="outline" onClick={() => { setShowUserForm(false); setEditingUser(null); setUserForm({ phone: '', name: '', role: 'outlet_staff', outlet_id: '' }); }}>Cancel</Button>
                    </div>
                  </form>
                </div>
              )}

              <div className="p-6">
                <div className="space-y-3">
                  {users.map((u) => (
                    <div key={u.phone || u.email || u.user_id} className="flex justify-between items-center p-4 bg-stone-50 rounded-sm" data-testid={`user-row-${u.phone || u.email}`}>
                      <div>
                        <div className="font-semibold text-stone-900">{u.name}</div>
                        <div className="text-sm text-stone-600 font-mono">{u.phone || u.email}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-2.5 py-1 bg-secondary/10 text-stone-700 rounded-sm text-xs font-medium flex items-center gap-1"><MapPin size={12} /> {locName(u.outlet_id)}</span>
                        <span className="px-3 py-1 bg-primary/10 text-primary rounded-sm text-sm font-medium uppercase">
                          {u.role}
                        </span>
                        <button data-testid={`edit-user-${u.phone || u.email}`} onClick={() => startEditUser(u)} className="text-stone-400 hover:text-primary"><Pencil size={16} /></button>
                        <button data-testid={`delete-user-${u.phone || u.email}`} onClick={() => handleDeleteUser(u)} className="text-stone-400 hover:text-functional-error"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="locations">
            <div className="bg-white border border-stone-200 rounded-sm shadow-sm">
              <div className="p-6 border-b border-stone-200 flex justify-between items-center">
                <h2 className="font-heading text-2xl font-semibold text-stone-800">Locations</h2>
                <Button onClick={() => setShowLocationForm(!showLocationForm)} data-testid="add-location-button">
                  <Plus size={20} className="mr-2" />
                  Add Location
                </Button>
              </div>

              {showLocationForm && (
                <div className="p-6 border-b border-stone-200 bg-stone-50">
                  <form onSubmit={handleCreateLocation} className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Location ID"
                      data-testid="location-id-input"
                      value={locationForm.location_id}
                      onChange={(e) => setLocationForm({ ...locationForm, location_id: e.target.value })}
                      className="h-10 px-3 border border-stone-200 rounded-sm disabled:bg-stone-100 disabled:text-stone-400"
                      required={!editingLocation}
                      disabled={!!editingLocation}
                    />
                    <input
                      type="text"
                      placeholder="Name"
                      data-testid="location-name-input"
                      value={locationForm.name}
                      onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                      className="h-10 px-3 border border-stone-200 rounded-sm"
                      required
                    />
                    <Select value={locationForm.type} onValueChange={(value) => setLocationForm({ ...locationForm, type: value })}>
                      <SelectTrigger data-testid="location-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="central_kitchen">Central Kitchen</SelectItem>
                        <SelectItem value="outlet">Outlet</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Button type="submit" data-testid="save-location-button">{editingLocation ? 'Update' : 'Save'}</Button>
                      <Button type="button" variant="outline" onClick={() => { setShowLocationForm(false); setEditingLocation(null); setLocationForm({ location_id: '', name: '', type: 'outlet' }); }}>Cancel</Button>
                    </div>
                  </form>
                </div>
              )}

              <div className="p-6">
                <div className="space-y-3">
                  {locations.map((loc) => (
                    <div key={loc.location_id} className="flex justify-between items-center p-4 bg-stone-50 rounded-sm" data-testid={`location-row-${loc.location_id}`}>
                      <div>
                        <div className="font-semibold text-stone-900">{loc.name}</div>
                        <div className="text-sm text-stone-600 font-mono">{loc.location_id}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-3 py-1 bg-secondary/10 text-secondary rounded-sm text-sm font-medium capitalize">
                          {loc.type.replace('_', ' ')}
                        </span>
                        {loc.location_id !== 'in_transit' && (
                          <>
                            <button data-testid={`edit-location-${loc.location_id}`} onClick={() => startEditLocation(loc)} className="text-stone-400 hover:text-primary"><Pencil size={16} /></button>
                            <button data-testid={`delete-location-${loc.location_id}`} onClick={() => handleDeleteLocation(loc)} className="text-stone-400 hover:text-functional-error"><Trash2 size={16} /></button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="products">
            <div className="bg-white border border-stone-200 rounded-sm shadow-sm">
              <div className="p-6 border-b border-stone-200 flex justify-between items-center">
                <h2 className="font-heading text-2xl font-semibold text-stone-800">Products</h2>
                <Button onClick={() => setShowProductForm(!showProductForm)} data-testid="add-product-button">
                  <Plus size={20} className="mr-2" />
                  Add Product
                </Button>
              </div>

              {showProductForm && (
                <div className="p-6 border-b border-stone-200 bg-stone-50">
                  <form onSubmit={handleCreateProduct} className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Product ID"
                      data-testid="product-id-input"
                      value={productForm.product_id}
                      onChange={(e) => setProductForm({ ...productForm, product_id: e.target.value })}
                      className="h-10 px-3 border border-stone-200 rounded-sm disabled:bg-stone-100 disabled:text-stone-400"
                      required={!editingProduct}
                      disabled={!!editingProduct}
                    />
                    <input
                      type="text"
                      placeholder="Name"
                      data-testid="product-name-input"
                      value={productForm.name}
                      onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                      className="h-10 px-3 border border-stone-200 rounded-sm"
                      required
                    />
                    <Select value={productForm.category} onValueChange={(value) => setProductForm({ ...productForm, category: value })}>
                      <SelectTrigger data-testid="product-category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="raw_material">Raw Material</SelectItem>
                        <SelectItem value="packaging">Packaging</SelectItem>
                        <SelectItem value="sponge">Sponge</SelectItem>
                        <SelectItem value="buttercream">Buttercream</SelectItem>
                        <SelectItem value="filling">Filling</SelectItem>
                      </SelectContent>
                    </Select>
                    <input
                      type="text"
                      placeholder="Unit"
                      data-testid="product-unit-input"
                      value={productForm.unit}
                      onChange={(e) => setProductForm({ ...productForm, unit: e.target.value })}
                      className="h-10 px-3 border border-stone-200 rounded-sm"
                      required
                    />
                    <div className="col-span-2 flex gap-2">
                      <Button type="submit" data-testid="save-product-button">{editingProduct ? 'Update' : 'Save'}</Button>
                      <Button type="button" variant="outline" onClick={() => { setShowProductForm(false); setEditingProduct(null); setProductForm({ product_id: '', name: '', category: 'raw_material', unit: 'kg', sponge_size: '', sponge_base: 'chocolate' }); }}>Cancel</Button>
                    </div>
                  </form>
                </div>
              )}

              <div className="p-6">
                <div className="space-y-3">
                  {products.map((prod) => (
                    <div key={prod.product_id} className="flex justify-between items-center p-4 bg-stone-50 rounded-sm" data-testid={`product-row-${prod.product_id}`}>
                      <div>
                        <div className="font-semibold text-stone-900">{prod.name}</div>
                        <div className="text-sm text-stone-600 font-mono">{prod.product_id}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <span className="px-3 py-1 bg-functional-info/10 text-functional-info rounded-sm text-sm font-medium capitalize">
                            {prod.category.replace('_', ' ')}
                          </span>
                          <div className="text-xs text-stone-500 mt-1 font-mono">{prod.unit}</div>
                        </div>
                        <button data-testid={`edit-product-${prod.product_id}`} onClick={() => startEditProduct(prod)} className="text-stone-400 hover:text-primary"><Pencil size={16} /></button>
                        <button data-testid={`delete-product-${prod.product_id}`} onClick={() => handleDeleteProduct(prod)} className="text-stone-400 hover:text-functional-error"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="access">
            <div className="bg-white border border-stone-200 rounded-sm shadow-sm">
              <div className="p-6 border-b border-stone-200">
                <h2 className="font-heading text-2xl font-semibold text-stone-800 flex items-center gap-2">
                  <ShieldCheck size={22} className="text-secondary" /> Google Login Access
                </h2>
                <p className="text-stone-600 text-sm mt-1">Only the email addresses below can sign in with "Continue with Google". Add your team's Google emails and assign each a role.</p>
              </div>

              <div className="p-6 border-b border-stone-200 bg-stone-50">
                <form onSubmit={handleAddAllow} className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <input
                    type="email"
                    placeholder="Google email"
                    data-testid="allow-email-input"
                    value={allowForm.email}
                    onChange={(e) => setAllowForm({ ...allowForm, email: e.target.value })}
                    className="h-10 px-3 border border-stone-200 rounded-sm disabled:bg-stone-100 disabled:text-stone-400"
                    required
                    disabled={editingAllow}
                  />
                  <input
                    type="text"
                    placeholder="Name (optional)"
                    data-testid="allow-name-input"
                    value={allowForm.name}
                    onChange={(e) => setAllowForm({ ...allowForm, name: e.target.value })}
                    className="h-10 px-3 border border-stone-200 rounded-sm"
                  />
                  <Select value={allowForm.role} onValueChange={(value) => setAllowForm({ ...allowForm, role: value })}>
                    <SelectTrigger data-testid="allow-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="central_kitchen">Central Kitchen</SelectItem>
                      <SelectItem value="outlet_staff">Outlet Staff</SelectItem>
                      <SelectItem value="finance">Finance</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={allowForm.outlet_id || undefined} onValueChange={(value) => setAllowForm({ ...allowForm, outlet_id: value })}>
                    <SelectTrigger data-testid="allow-location-select">
                      <SelectValue placeholder="Location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.filter((l) => ['outlet', 'central_kitchen'].includes(l.type)).map((l) => (
                        <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="submit" data-testid="save-allow-button">
                    <Plus size={18} className="mr-2" /> {editingAllow ? 'Update' : 'Grant Access'}
                  </Button>
                </form>
                {editingAllow && (
                  <button data-testid="cancel-allow-edit" onClick={() => { setEditingAllow(false); setAllowForm({ email: '', name: '', role: 'outlet_staff', outlet_id: '' }); }} className="text-sm text-stone-500 hover:text-stone-800 mt-3">Cancel edit</button>
                )}
              </div>

              <div className="p-6">
                <div className="space-y-3">
                  {allowlist.map((a) => (
                    <div key={a.email} className="flex justify-between items-center p-4 bg-stone-50 rounded-sm" data-testid={`allow-row-${a.email}`}>
                      <div>
                        <div className="font-semibold text-stone-900">{a.name || a.email}</div>
                        <div className="text-sm text-stone-600 font-mono">{a.email}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-2.5 py-1 bg-secondary/10 text-stone-700 rounded-sm text-xs font-medium flex items-center gap-1"><MapPin size={12} /> {locName(a.outlet_id)}</span>
                        <span className="px-3 py-1 bg-primary/10 text-primary rounded-sm text-sm font-medium uppercase">{a.role}</span>
                        <button data-testid={`edit-allow-${a.email}`} onClick={() => startEditAllow(a)} className="text-stone-400 hover:text-primary">
                          <Pencil size={18} />
                        </button>
                        <button
                          data-testid={`delete-allow-${a.email}`}
                          onClick={() => handleDeleteAllow(a.email)}
                          className="text-stone-400 hover:text-functional-error"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                  {allowlist.length === 0 && (
                    <p className="text-stone-400 text-center py-8 text-sm">No emails added yet. Add your Google email above to enable Google login.</p>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="danger">
            <div className="max-w-2xl">
              <div className="border border-functional-error/30 bg-functional-error/5 rounded-sm p-6">
                <div className="flex items-start gap-3 mb-4">
                  <AlertTriangle className="text-functional-error shrink-0 mt-0.5" size={22} />
                  <div>
                    <h3 className="font-semibold text-stone-900 text-lg">Clear POS Sales Test Data</h3>
                    <p className="text-stone-600 text-sm mt-1">
                      Permanently deletes <b>all POS sales/bills</b> and reverses the inventory deductions made by those sales.
                      Use this once to wipe test billing before going live. This does <b>not</b> touch orders, customers,
                      menu, products, locations, users or settings.
                    </p>
                  </div>
                </div>
                <p className="text-sm font-medium text-stone-700 mb-1">Type <span className="font-mono bg-stone-200 px-1.5 py-0.5 rounded">CLEAR</span> to confirm</p>
                <div className="flex gap-2">
                  <Input
                    data-testid="clear-confirm-input"
                    value={clearConfirm}
                    onChange={(e) => setClearConfirm(e.target.value)}
                    placeholder="CLEAR"
                    className="max-w-[200px]"
                  />
                  <Button
                    data-testid="clear-pos-sales-button"
                    onClick={clearPosSales}
                    disabled={clearConfirm !== 'CLEAR' || clearing}
                    className="bg-functional-error hover:bg-functional-error/90 text-white disabled:opacity-50"
                  >
                    <Trash2 size={16} className="mr-2" /> {clearing ? 'Clearing...' : 'Clear POS Sales'}
                  </Button>
                </div>
              </div>

              <div className="border border-functional-error/30 bg-functional-error/5 rounded-sm p-6 mt-6">
                <div className="flex items-start gap-3 mb-4">
                  <AlertTriangle className="text-functional-error shrink-0 mt-0.5" size={22} />
                  <div>
                    <h3 className="font-semibold text-stone-900 text-lg">Clear Test &amp; Orphan Inventory Data</h3>
                    <p className="text-stone-600 text-sm mt-1">
                      Removes leftover <b>test products / menu items</b> (anything named <span className="font-mono">TEST_…</span> or "Test Product")
                      and <b>orphan stock records</b> (stock at deleted/invalid locations or for products that no longer exist).
                      Your real products, stock, orders, customers and settings are <b>not</b> touched.
                    </p>
                  </div>
                </div>
                <p className="text-sm font-medium text-stone-700 mb-1">Type <span className="font-mono bg-stone-200 px-1.5 py-0.5 rounded">CLEAR</span> to confirm</p>
                <div className="flex gap-2">
                  <Input
                    data-testid="clear-inv-confirm-input"
                    value={invConfirm}
                    onChange={(e) => setInvConfirm(e.target.value)}
                    placeholder="CLEAR"
                    className="max-w-[200px]"
                  />
                  <Button
                    data-testid="clear-inventory-button"
                    onClick={clearInventoryTestData}
                    disabled={invConfirm !== 'CLEAR' || clearingInv}
                    className="bg-functional-error hover:bg-functional-error/90 text-white disabled:opacity-50"
                  >
                    <Trash2 size={16} className="mr-2" /> {clearingInv ? 'Clearing...' : 'Clear Inventory Test Data'}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
